import React, { useState } from 'react';
import { UserIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
interface ProfileProps {
  username: string;
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}
export function Profile({
  username,
  onChangePassword
}: ProfileProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Udfyld venligst alle felter');
      return;
    }
    if (newPassword.length < 6) {
      setError('Ny adgangskode skal være mindst 6 tegn');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Nye adgangskoder matcher ikke');
      return;
    }
    if (currentPassword === newPassword) {
      setError('Ny adgangskode skal være forskellig fra den nuværende');
      return;
    }
    setIsLoading(true);
    try {
      const result = await onChangePassword(currentPassword, newPassword);
      if (result) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('Nuværende adgangskode er forkert');
      }
    } catch (err) {
      setError('Der opstod en fejl. Prøv igen.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="max-w-2xl mx-auto" data-id="element-1590" data-mp="3jw9aolnnpo5r">
      <header className="mb-8" data-id="element-1591" data-mp="3out0ix5dfk3e">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1592" data-mp="3oujaj9kbtcxp">Profil</h1>
        <p className="text-stone-600" data-id="element-1593" data-mp="3tl9d6f1db1op">Administrer dine kontooplysninger</p>
      </header>

      <div className="space-y-6" data-id="element-1594" data-mp="2f87km0690ag">
        {/* Username Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1595" data-mp="2eyhyzj1gv5a">
          <div className="flex items-center gap-3 mb-4" data-id="element-1596" data-mp="gcn03zlq1o6n">
            <div className="p-2 bg-teal-100 rounded-lg" data-id="element-1597" data-mp="2d8krjxtlg26t">
              <UserIcon className="w-5 h-5 text-teal-700" data-id="element-1598" data-mp="2i645sxqakeyr" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900" data-id="element-1599" data-mp="2i876qcszfsi9">Brugernavn</h2>
          </div>

          <div className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-1600" data-mp="2aoo7cvzflmni">
            <p className="text-sm text-stone-500 mb-1" data-id="element-1601" data-mp="25utotvvkbdq0">Dit brugernavn</p>
            <p className="text-lg font-medium text-stone-900" data-id="element-1602" data-mp="20y39xbvhuo66">{username}</p>
            <p className="text-xs text-stone-400 mt-2" data-id="element-1603" data-mp="1vygwb1br75wu">
              Brugernavnet kan ikke ændres
            </p>
          </div>
        </motion.div>

        {/* Change Password Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1604" data-mp="2omw0stx9e5tx">
          <div className="flex items-center gap-3 mb-6" data-id="element-1605" data-mp="189fjl1vw18lr">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1606" data-mp="1399ofbqet3yx">
              <KeyIcon className="w-5 h-5 text-blue-700" data-id="element-1607" data-mp="137q6zht0omvk" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900" data-id="element-1608" data-mp="2s376d5kfqk34">
              Skift adgangskode
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1609" data-mp="2wvqp0gxzc5es">
            {/* Current Password */}
            <div data-id="element-1610" data-mp="2i7484aspf5lg">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1611" data-mp="2i3gd177m2b4m">
                Nuværende adgangskode
              </label>
              <div className="relative" data-id="element-1612" data-mp="3atp0nekx1nql">
                <input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast nuværende adgangskode" data-id="element-1613" data-mp="3auq8792q3s3k" />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1614" data-mp="1udm4h1ubr8q6">
                  {showCurrentPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1615" data-mp="hhg62avozazg" /> : <EyeIcon className="w-5 h-5" data-id="element-1616" data-mp="hfc4w0p6gq2v" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div data-id="element-1617" data-mp="3kz6scen8jvd6">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1618" data-mp="3kzq57gy13tkc">
                Ny adgangskode
              </label>
              <div className="relative" data-id="element-1619" data-mp="3tytqmrko4l20">
                <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast ny adgangskode (min. 6 tegn)" data-id="element-1620" data-mp="3txs7dzti0s97" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1621" data-mp="1ex2za0ds2jpn">
                  {showNewPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1622" data-mp="r78m1hpjqt4v" /> : <EyeIcon className="w-5 h-5" data-id="element-1623" data-mp="w5a3xzb8kmer" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div data-id="element-1624" data-mp="27lrtrgkbvqsf">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1625" data-mp="27k70du9r2dw5">
                Bekræft ny adgangskode
              </label>
              <div className="relative" data-id="element-1626" data-mp="2cm62z1xo16s1">
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast ny adgangskode igen" data-id="element-1627" data-mp="x9cw8mrnnr6" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1628" data-mp="39bq9ej3fo9ie">
                  {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1629" data-mp="12lk515j0kb56" /> : <EyeIcon className="w-5 h-5" data-id="element-1630" data-mp="xoa4atq6cd21" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1631" data-mp="bfxymkb7bio6">
                <p className="text-sm text-red-700" data-id="element-1632" data-mp="3jv7xljjso7rq">{error}</p>
              </motion.div>}

            {/* Success Message */}
            {success && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-id="element-1633" data-mp="39zens7xgmsdn">
                <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-1634" data-mp="1d48rjjf9wa38" />
                <p className="text-sm text-emerald-700" data-id="element-1635" data-mp="2xp5hvajqydmm">
                  Adgangskode ændret succesfuldt!
                </p>
              </motion.div>}

            {/* Submit Button */}
            <div className="pt-2" data-id="element-1636" data-mp="37eytucnswure">
              <Button type="submit" variant="primary" disabled={isLoading} className="w-full" data-id="element-1637" data-mp="37g9sxz6axec6">
                {isLoading ? 'Opdaterer...' : 'Skift adgangskode'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}